import { FormEvent, useCallback, useMemo } from "react";
import { TextInput, Textarea, Select, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { SourceSubsite } from "../../../services/admin/sourcesubsites.service";
import { required, scrollToFirstError } from "../../../utils/formValidation";

export interface SourceSubsiteFormValues {
  name: string;
  description: string;
  sourceId: string;
}

interface SourceSubsiteFormProps {
  initialValues?: SourceSubsite;
  onSubmit: (values: SourceSubsiteFormValues) => void;
  sources: Array<{ id: string; name: string; isActive?: boolean }>;
  preselectedSourceId?: string;
  initialName?: string;
  existingSubsiteNamesBySource?: Record<string, string[]>;
  formId?: string;
  autoFocusName?: boolean;
}

const duplicateSubsite =
  (existingNames: string[], sourceName: string) =>
  (value: string | undefined) =>
    existingNames.some(
      (name) => name.trim().toLowerCase() === value?.trim().toLowerCase()
    )
      ? `This subsite already exists for ${sourceName}`
      : null;

export default function SourceSubsiteForm({
  initialValues,
  onSubmit,
  sources,
  preselectedSourceId,
  initialName = "",
  existingSubsiteNamesBySource = {},
  formId,
  autoFocusName = false,
}: SourceSubsiteFormProps) {
  const validateName = useCallback(
    (value: string, values: SourceSubsiteFormValues) => {
      const requiredError = required("Subsite name is required")(value);
      if (requiredError) return requiredError;

      const sourceName =
        sources.find(({ id }) => id === values.sourceId)?.name ||
        "the selected source";
      return duplicateSubsite(
        existingSubsiteNamesBySource[values.sourceId] || [],
        sourceName
      )(value);
    },
    [existingSubsiteNamesBySource, sources]
  );

  const form = useForm<SourceSubsiteFormValues>({
    validateInputOnBlur: true,
    initialValues: {
      name: initialValues?.name || initialName,
      description: initialValues?.description || "",
      sourceId: initialValues?.sourceId || preselectedSourceId || "",
    },
    validate: {
      name: validateName,
      sourceId: required("Subsite must belong to a source"),
    },
  });

  const sourceOptions = useMemo(
    () =>
      sources
        .filter((source) => source.isActive !== false)
        .map((source) => ({ value: source.id, label: source.name }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [sources]
  );

  const handleValidationError = useCallback(() => {
    scrollToFirstError(form);
  }, [form]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      form.onSubmit(onSubmit, handleValidationError)(event);
    },
    [form, handleValidationError, onSubmit]
  );

  return (
    <form id={formId} noValidate onSubmit={handleSubmit}>
      <Stack gap="md">
        <TextInput
          label="Subsite Name"
          placeholder="e.g., Right Upper Lobe, Left Atrium"
          withAsterisk
          autoFocus={autoFocusName}
          {...form.getInputProps("name")}
        />

        <Select
          label="Parent Source"
          placeholder="Select a source"
          data={sourceOptions}
          searchable
          withAsterisk
          {...form.getInputProps("sourceId")}
        />

        <Textarea
          label="Description"
          placeholder="Optional context or synonyms"
          minRows={3}
          {...form.getInputProps("description")}
        />
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
